import { useState } from 'react';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import dollarIcon from 'src/assets/images/icons/dollar.svg';
import calendarGreyIcon from 'src/assets/images/icons/calendar-grey.svg';
import containerIcon from 'src/assets/images/icons/container.svg';
import consigneeIcon from 'src/assets/images/icons/consignee.svg';
import instanceIcon from 'src/assets/images/icons/instance.svg';
import shipperIcon from 'src/assets/images/icons/shipper.svg';
import weightIcon from 'src/assets/images/icons/weight.svg';
import information from 'src/assets/images/icons/info-circle-grey-3.svg';
import { Tooltip } from '@mui/material';
import { CO2, M3, Kg, Ton } from 'src/presentations/components/atom/unit';
import { displayValueOrFallback, formatNumber, transformTitle, beautifyEnum } from 'src/utils/utility';
import { useInvoiceSummaryFromParams } from 'src/use-cases/invoice/useInvoices';
import ShipmentInfoCard from 'src/presentations/components/molecules/card/ShipmentInfoCard';
import { useShipmentSelector } from '../ShipmentSelector';
import TransportSummary from './transportSummary';
import ChargeDetailsModal from '../chargeDetails/ChargeDetailsModal';

const cardContainerStyle = {
  bgcolor: '#fff',
  borderRadius: '10px',
  border: '1px solid #DFDFDF',
  padding: '12px 12px 8px 12px',
  mt: '10px',
};
const cardContentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};
const cardContentStyleDate = {
  fontWeight: 700,
  color: '#8A92A6',
  fontSize: '16px',
  lineHeight: '24px',
};

const smallFontStyle = {
  fontWeight: 400,
  fontSize: '10px',
  lineHeight: '16px',
};

const mediumFontStyle = {
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#8A92A6',
};

const ShipmentOverview = () => {
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { shipment } = useShipmentSelector();
  const shipmentDetail = shipment?.shipmentDetail as any; // Type assertion for linkedShipmentInvoices
  const [searchParams] = useSearchParams();
  const [isChargeDetailsModalOpen, setIsChargeDetailsModalOpen] = useState(false);

  // Get provider and modality from URL params or invoiceSummary
  const provider = searchParams.get('provider') || invoiceSummary?.serviceProvider || '';
  const modality = searchParams.get('modality') || invoiceSummary?.modality || '';

  // Handler for clicking on invoice link - opens in new tab
  const handleInvoiceClick = (invoiceId: string) => {
    if (!invoiceId) return; // Guard against undefined

    const params = new URLSearchParams();
    if (provider) params.set('provider', provider);
    if (modality) params.set('modality', modality);
    const queryString = params.toString();
    const path = `/action-details/${invoiceId}`;
    const url = queryString ? `${path}?${queryString}` : path;

    window.open(url, '_blank');
  };

  // Handler for opening charge details modal
  const handleOpenChargeDetails = () => {
    setIsChargeDetailsModalOpen(true);
  };

  const handleCloseChargeDetails = () => {
    setIsChargeDetailsModalOpen(false);
  };

  return (
    <>
      {shipment?.transportDetail && (
        <div className='mb-24'>
          <TransportSummary data={shipment} />
        </div>
      )}
      <div data-testid='top-shipment-overview-container' className='flex'>
        {/* start: Top Card Shipmen Overview */}
        <div className='gap-10 grid-cols-2 grid w-full'>
          {/* start: Total Amount invoices card */}
          <ShipmentInfoCard
            icon={dollarIcon}
            title='Total Amount Invoices / Deviations'
            subtitle='Before Audit'
            testId='Total-Amount-Invoices-Deviations'
            rows={[
              {
                id: 'before-tax',
                label: `Before Tax - ${shipmentDetail?.shipmentAmount?.currency}`,
                value: (
                  <>
                    <span className='text-12 xl:text-16'>{formatNumber(shipmentDetail?.shipmentAmount?.beforeTax)}</span> /{' '}
                    <span className='text-12 xl:text-16 text-red-40'>{formatNumber(shipmentDetail?.deviationAmount?.beforeTax) || '00.00'}</span>
                  </>
                ),
              },
              {
                id: 'after-tax',
                label: `After Tax - ${shipmentDetail?.shipmentAmount?.currency}`,
                value: (
                  <>
                    <span className='text-12 xl:text-16'>{formatNumber(shipmentDetail?.shipmentAmount?.afterTax)}</span> /{' '}
                    <span className='text-12 xl:text-16 text-red-40'>{formatNumber(shipmentDetail?.deviationAmount?.afterTax) || '00.00'}</span>
                  </>
                ),
              },
              {
                id: 'number-of-invoices',
                label: 'Number Of Invoice',
                value: shipmentDetail?.numberOfInvoices,
                showDivider: !!shipmentDetail?.linkedShipmentInvoices?.length,
              },
              ...(shipmentDetail?.linkedShipmentInvoices?.length
                ? [
                    {
                      id: 'linked-invoices',
                      label: '',
                      value: (
                        <div className='flex flex-wrap gap-8'>
                          {shipmentDetail.linkedShipmentInvoices.map((invoice: any, index: number) => (
                            <button
                              key={invoice.invoiceId || index}
                              onClick={() => handleInvoiceClick(invoice.invoiceId)}
                              className='text-12 xl:text-14 text-[#4A4A4A] underline hover:text-[#00992B] transition-colors cursor-pointer'
                              type='button'
                            >
                              {invoice.invoiceNumber || invoice.invoiceId || 'N/A'}
                            </button>
                          ))}
                        </div>
                      ),
                      showDivider: false,
                    },
                  ]
                : []),
            ]}
          />

          {/* start: Total GW */}
          <ShipmentInfoCard
            icon={weightIcon}
            title={
              <>
                Total Charg. Weight / Vol. / <CO2 />
              </>
            }
            rows={[
              {
                id: 'total-chargeable-weight',
                label: 'Total Chargeable Weight',
                value: (
                  <div className='flex items-center gap-8'>
                    <span>
                      {displayValueOrFallback(shipmentDetail?.totalChargeableWeight)} {shipmentDetail?.totalChargeableWeight ? <Kg /> : ''}
                    </span>
                    {shipmentDetail?.chargeableWeightDescription && (
                      <Tooltip
                        title={shipmentDetail.chargeableWeightDescription}
                        arrow
                        placement='bottom'
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#000',
                              color: 'white',
                              '& .MuiTooltip-arrow': {
                                color: '#000',
                              },
                            },
                          },
                        }}
                      >
                        <img src={information} alt='information' className='w-16 h-16 cursor-pointer opacity-60 hover:opacity-100 transition-opacity' />
                      </Tooltip>
                    )}
                  </div>
                ),
              },
              {
                id: 'volume',
                label: 'Volume',
                value: (
                  <>
                    {displayValueOrFallback(shipmentDetail?.volume)} {shipmentDetail?.volume ? <M3 /> : ''}
                  </>
                ),
              },
              {
                id: 'number-of-packages',
                label: 'Number of Packages',
                value: displayValueOrFallback(shipmentDetail?.numberOfPackagesDescription),
              },
              {
                id: 'co2',
                label: (
                  <>
                    Total <CO2 />
                  </>
                ),
                value: (
                  <>
                    {shipmentDetail?.totalCo2Emission === 0 ? '0' : displayValueOrFallback(shipmentDetail?.totalCo2Emission)}
                    {shipmentDetail?.totalCo2Emission ? <Ton /> : ''}
                  </>
                ),
                showDivider: false,
              },
            ]}
          />
        </div>
      </div>

      {/* start: Midle Card Inovice Details */}
      <div className='gap-10 grid-cols-2 grid w-full'>
        <ShipmentInfoCard
          icon={containerIcon}
          title='Charges'
          actions={
            <div className='ml-auto'>
              <button
                type='button'
                className='text-10 xl:text-12 text-primary font-medium hover:text-primary-dark transition-colors'
                onClick={handleOpenChargeDetails}
              >
                See Details
              </button>
            </div>
          }
          rows={[
            {
              id: 'total-no-charges',
              label: 'Total No. of Charges',
              value: shipment?.invoiceCharges?.length ?? 0,
            },
            {
              id: 'with-deviations',
              label: 'With Deviations',
              value: <span className='text-red-40'>{shipment?.deviations?.length ?? 0}</span>,
            },
            {
              id: 'Approved by Freya',
              label: 'Approved by Freya',
              value: (
                <span className='text-primary'>
                  {shipment?.currentDeviationWorkflow === 'PROCUREMENT_NO_CONTRACT_REVIEW'
                    ? 0
                    : shipment?.invoiceCharges?.length > 0
                      ? shipment?.invoiceCharges?.length - shipment?.deviations?.length
                      : 0}
                </span>
              ),
            },
          ]}
        />
        <ShipmentInfoCard icon={instanceIcon} title='Service Type'>
          <div className='font-bold text-12 xl:text-16 leading-[20px] xl:leading-[24px] text-black mt-6 flex items-center flex-wrap'>
            {shipmentDetail?.serviceType && (
              <>
                {/* First part with circular separators */}
                {displayValueOrFallback(shipmentDetail?.incoterm, '')}
                {shipmentDetail?.incoterm && shipmentDetail?.serviceType?.modality && <span className='inline-block w-4 h-4 mx-4 rounded-full bg-[#8A92A6]' />}

                {displayValueOrFallback(shipmentDetail?.serviceType?.modality, '')}
                {shipmentDetail?.serviceType?.modality && shipmentDetail?.serviceType?.importExportType && (
                  <span className='inline-block w-4 h-4 mx-4 rounded-full bg-[#8A92A6]' />
                )}

                {displayValueOrFallback(shipmentDetail?.serviceType?.importExportType, '')}

                {/* Add pipe separator if there are values on both sides */}
                {[shipmentDetail?.incoterm, shipmentDetail?.serviceType?.modality, shipmentDetail?.serviceType?.importExportType].some(Boolean) &&
                  [shipmentDetail?.serviceType?.name, shipmentDetail?.serviceType?.specialType].some(Boolean) &&
                  ' | '}

                {/* Second part with circular separators */}
                {displayValueOrFallback(shipmentDetail?.serviceType?.name, '')}
                {shipmentDetail?.serviceType?.name && shipmentDetail?.serviceType?.specialType && (
                  <span className='inline-block w-4 h-4 mx-4 rounded-full bg-[#8A92A6]' />
                )}

                {displayValueOrFallback(shipmentDetail?.serviceType?.specialType, '')}
              </>
            )}
          </div>
          <div className='text-10 xl:text-12 font-normal leading-[16px] mt-8 pt-8 border-t border-neutral-20 w-full'>
            {invoiceSummary?.modality === 'SEA' && (
              <div className={`flex justify-between ${shipmentDetail?.orderTime ? 'pb-4 border-b border-neutral-20' : ''}`}>
                <span>{shipmentDetail?.containersCount ?? 0} Container</span>
                <span>
                  {shipmentDetail?.containers?.map((item, index) => (
                    <span key={`container-service-${item.type}-${item.size}-${index}`}>
                      {index > 0 && ', '}
                      {item?.quantity} x {item?.size}
                      {item?.type}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {shipmentDetail?.orderTime && (
              <div className='flex justify-between items-center pt-4'>
                <div className='flex items-center gap-4'>
                  <img src={calendarGreyIcon} alt='calendar' className='w-12 h-12' />
                  <span>Order Time</span>
                </div>
                <span>{format(parseISO(shipmentDetail.orderTime), 'd-MMM-yyyy HH:mm')}</span>
              </div>
            )}
          </div>
        </ShipmentInfoCard>
      </div>

      {/* start: Bottom Card Inovice Details */}
      <div className='gap-10 grid-cols-2 grid w-full'>
        <ShipmentInfoCard
          icon={shipperIcon}
          title='Shipper / Pick-Up Place'
          rows={[
            {
              id: 'shipper',
              label: <span className='min-w-[85px] inline-block "text-10 xl:text-12  leading-[16px]'>Shipper</span>,
              value: (
                <span className='font-bold text-12 xl:text-16 leading-[14px] xl:leading-[18px] text-black uppercase'>
                  {beautifyEnum(shipmentDetail.shipperCode)}
                </span>
              ),
            },
            {
              id: 'pickup-place',
              label: <span className='"text-10 xl:text-12 min-w-[85px] inline-block  leading-[16px]'>Pick Up Place</span>,
              value: (
                <span className='font-bold text-12 xl:text-16 leading-[14px] xl:leading-[18px] text-black'>{transformTitle(shipmentDetail.placeToPickup)}</span>
              ),
            },
          ]}
        />
        <ShipmentInfoCard
          icon={consigneeIcon}
          title='Consignee / Delivery Place'
          rows={[
            {
              id: 'consignee',
              label: <span className='min-w-[85px] inline-block "text-10 xl:text-12  leading-[16px]'>Consignee</span>,
              value: (
                <span className='font-bold text-12 xl:text-16 leading-[14px] xl:leading-[18px] text-black max-w-[200px] break-words inline-block uppercase'>
                  {beautifyEnum(shipmentDetail.consigneeCode)}
                </span>
              ),
            },
            {
              id: 'delivery-place',
              label: <span className='min-w-[85px] inline-block "text-10 xl:text-12  leading-[16px]'>Delivery Place</span>,
              value: <span className='font-bold text-12 xl:text-16 leading-[14px] xl:leading-[18px] text-black'>{shipmentDetail.deliveryPlace}</span>,
            },
          ]}
        />
      </div>
      {/* Charge Details Modal */}
      <ChargeDetailsModal open={isChargeDetailsModalOpen} onClose={handleCloseChargeDetails} />
    </>
  );
};

export default ShipmentOverview;
